import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { sectionsApi } from "@/api";
import type { CreateSectionData, UpdateSectionData } from "@/api";
import { QUERY_KEYS } from "@/constants";

/** Kursning bo'limlari (tartiblangan). */
export const useSections = (courseId: number | undefined) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_SECTIONS, courseId],
    queryFn: () => sectionsApi.getByCourse(courseId as number),
    enabled: !!courseId,
    select: res => res.data,
  });

const useInvalidateCurriculum = (courseId: number) => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_SECTIONS, courseId] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_COURSE_READINESS, courseId] });
  };
};

export const useCreateSection = (courseId: number) => {
  const { t } = useTranslation();
  const invalidate = useInvalidateCurriculum(courseId);
  return useMutation({
    mutationFn: (data: CreateSectionData) => sectionsApi.create(data),
    onSuccess: () => {
      invalidate();
      toast.success(t("COMMON.ADDED"));
    },
  });
};

export const useUpdateSection = (courseId: number) => {
  const { t } = useTranslation();
  const invalidate = useInvalidateCurriculum(courseId);
  return useMutation({
    mutationFn: (vars: { id: number; data: UpdateSectionData }) =>
      sectionsApi.update(vars.id, vars.data),
    onSuccess: () => {
      invalidate();
      toast.success(t("COMMON.UPDATED"));
    },
  });
};

export const useDeleteSection = (courseId: number) => {
  const { t } = useTranslation();
  const invalidate = useInvalidateCurriculum(courseId);
  return useMutation({
    mutationFn: (id: number) => sectionsApi.remove(id),
    onSuccess: () => {
      invalidate();
      toast.success(t("COMMON.DELETED"));
    },
  });
};

/**
 * Bo'limlarni qayta tartiblash.
 *
 * `sectionIds` — kursdagi BARCHA bo'limlar yangi tartibda. Qisman ro'yxat
 * yuborish eski ilovaning xatosi edi: backend qolganlarining `order` ini
 * o'zgartirmasdi va ro'yxat aralashib ketardi.
 */
export const useReorderSections = (courseId: number) => {
  const invalidate = useInvalidateCurriculum(courseId);
  return useMutation({
    mutationFn: (sectionIds: number[]) => sectionsApi.reorder(sectionIds),
    onSuccess: invalidate,
  });
};

export const useToggleSectionPublish = (courseId: number) => {
  const invalidate = useInvalidateCurriculum(courseId);
  return useMutation({
    mutationFn: (id: number) => sectionsApi.togglePublish(id),
    onSuccess: res => {
      invalidate();
      if (res.message) toast.success(res.message);
    },
  });
};

export const useDuplicateSection = (courseId: number) => {
  const { t } = useTranslation();
  const invalidate = useInvalidateCurriculum(courseId);
  return useMutation({
    mutationFn: (id: number) => sectionsApi.duplicate(id),
    onSuccess: () => {
      invalidate();
      toast.success(t("COMMON.ADDED"));
    },
  });
};
